/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { Timeline } from "primereact/timeline";
import { Card } from "primereact/card";
import Sidebar from "../sidebar";
import { Dialog } from "primereact/dialog";
import { evidenciasDeInteraccion, formatFecha } from "../../utils";
import Boton from "../boton";
import Titulo from "../titulo";

/**
 * Componente de timeline para mostrar actividades, emisiones y solicitudes.
 *
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.actividades - Lista de actividades a mostrar
 * @param {Object} [props.solicitud] - Solicitud asociada (opcional)
 * @param {Object} [props.revisorSolicitud] - Revisor de la solicitud (opcional)
 */
export default function ActividadesTimeline({
  actividades,
  solicitud,
  revisorSolicitud,
  cambiosEstado,
  formacionPorAuditor,
  onDescargarEvidencia,
  // El detalle de una actividad se abre por defecto en un panel lateral de
  // dos tercios de pantalla (en el admin muestra pasos, hallazgos e
  // interacciones). El portal de clientes, que solo ve unas pocas fechas,
  // pide una ventana chica centrada que se ajusta a su contenido.
  detalleComoDialogo = false,
}) {
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarSolicitudVisible, setSidebarSolicitudVisible] = useState(false);
  const [evidenciaDescargando, setEvidenciaDescargando] = useState(null);

  const ContenedorDetalle = detalleComoDialogo ? Dialog : Sidebar;
  const propsDetalle = detalleComoDialogo
    ? {
        header: actividadSeleccionada?.actividadtipo?.nombre,
        style: { width: "min(30rem, 94vw)" },
        dismissableMask: true,
        draggable: false,
        resizable: false,
        blockScroll: true
      }
    : { position: "right", className: "w-full lg:w-8" };

  // El detalle repite nombre y fechas que ya están en la línea de tiempo; lo
  // que agrega son los pasos. En el modo compacto (portal de clientes, donde
  // la API no siempre los manda) el ojo solo aparece si hay pasos para ver.
  // En el admin se muestra siempre, como hasta ahora.
  const tieneMasDetalle = (actividad) =>
    !detalleComoDialogo || actividad?.actividadpaso?.length > 0;

  // Combinar solicitud, actividades y emisiones en una sola lista
  const eventos = [];

  // Agregar la solicitud si existe
  if (solicitud) {
    eventos.push({
      tipo: "solicitud",
      id: `solicitud-${solicitud.id}`,
      fecha: solicitud.fecha,
      nombre: "Solicitud",
      nombrefantasia: solicitud.nombrefantasia,
      procesada: solicitud.procesadabool,
      solicitud: solicitud, // Guardar referencia completa
    });
  }

  // Agregar actividades y emisiones
  if (actividades) {
    actividades.forEach((actividad) => {
      // Agregar la actividad
      eventos.push({
        tipo: "actividad",
        id: `actividad-${actividad.id}`,
        fecha: actividad.fechacreacion,
        nombre: actividad.actividadtipo.nombre,
        fechafinalizacion: actividad.fechafinalizacion,
        actividad: actividad, // Guardar referencia completa
      });

      // Agregar las emisiones de certificado de esta actividad
      if (
        actividad.emisioncertificado &&
        actividad.emisioncertificado.length > 0
      ) {
        actividad.emisioncertificado.forEach((emision) => {
          eventos.push({
            tipo: "emision",
            id: `emision-${emision.id}`,
            fecha: emision.fechaemision,
            nombre: "Emisión de certificado",
            notas: emision.notas,
          });
        });
      }
    });
  }

  // Agregar cambios de estado del certificado
  if (cambiosEstado) {
    cambiosEstado.forEach((cambio) => {
      eventos.push({
        tipo: "cambioEstado",
        id: `cambio-${cambio.id}`,
        fecha: cambio.fechacambio,
        nombre: cambio.nuevoestado?.nombre,
        notas: cambio.notas,
        vigente: cambio.nuevoestado?.vigentebool,
        bajadefinitiva: cambio.nuevoestado?.bajadefinitiva,
      });
    });
  }

  // Ordenar por fecha (más antigua primero)
  eventos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

  const verDetallesActividad = (actividad) => {
    setActividadSeleccionada(actividad);
    setSidebarVisible(true);
  };

  const verDetallesSolicitud = (solicitud) => {
    setSolicitudSeleccionada(solicitud);
    setSidebarSolicitudVisible(true);
  };

  const customizedContent = (evento) => {
    const finalizada =
      evento.tipo === "actividad" && !!evento.fechafinalizacion;
    const esEmision = evento.tipo === "emision";
    const esSolicitud = evento.tipo === "solicitud";

    // Para emisiones, solicitudes y cambios de estado, mostrar la fecha en content
    if (esEmision || esSolicitud || evento.tipo === "cambioEstado") {
      return (
        <div css={{ marginTop: "10px" }}>
          {evento.fecha && formatFecha(evento.fecha)}
        </div>
      );
    }

    // Para actividades, mostrar los detalles en content
    return (
      <Card className="mb-3">
        <div css={{ display: "flex", flexDirection: "column", gap: "0.75em" }}>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <h3 css={{ margin: 0, color: "black" }}>{evento.nombre}</h3>
            {tieneMasDetalle(evento.actividad) && (
              <Boton
                callback={() => verDetallesActividad(evento.actividad)}
                icono
                chico
                negro
              >
                <i className="pi pi-eye"></i>
              </Boton>
            )}
          </div>

          {finalizada && evento.fechafinalizacion && (
            <small className="text-500">
              <strong>Finalizada el:</strong>{" "}
              {formatFecha(evento.fechafinalizacion)}
            </small>
          )}
        </div>
      </Card>
    );
  };

  const customizedOpposite = (evento) => {
    const esEmision = evento.tipo === "emision";
    const esSolicitud = evento.tipo === "solicitud";

    // Para emisiones, mostrar los detalles en opposite
    if (esEmision) {
      return (
        <Card className="mb-3">
          <div
            css={{ display: "flex", flexDirection: "column", gap: "0.75em" }}
          >
            <h3 css={{ margin: 0, color: "black" }}>{evento.nombre}</h3>

            {evento.notas && <small className="text-500">{evento.notas}</small>}
          </div>
        </Card>
      );
    }

    // Para cambios de estado, mostrar los detalles en opposite
    if (evento.tipo === "cambioEstado") {
      const color = evento.vigente
        ? "var(--verde)"
        : evento.bajadefinitiva
          ? "var(--rojo)"
          : "var(--amarillo)";
      return (
        <Card className="mb-3">
          <div
            css={{ display: "flex", flexDirection: "column", gap: "0.5em" }}
          >
            <h3 css={{ margin: 0, color: "black" }}>Cambio de estado</h3>
            <span css={{ color, fontWeight: "600" }}>{evento.nombre}</span>
            {evento.notas && <small className="text-500">{evento.notas}</small>}
          </div>
        </Card>
      );
    }

    // Para solicitudes, mostrar los detalles en opposite
    if (esSolicitud) {
      return (
        <Card className="mb-3">
          <div
            css={{ display: "flex", flexDirection: "column", gap: "0.75em" }}
          >
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <h3 css={{ margin: 0, color: "black" }}>{evento.nombre}</h3>
              <Boton
                callback={() => verDetallesSolicitud(evento.solicitud)}
                icono
                chico
                negro
              >
                <i className="pi pi-eye"></i>
              </Boton>
            </div>
            {evento.nombrefantasia && (
              <small className="text-500">{evento.nombrefantasia}</small>
            )}
          </div>
        </Card>
      );
    }

    // Para actividades, mostrar la fecha en opposite
    return (
      <div css={{ marginTop: "10px" }}>
        {evento.fecha && formatFecha(evento.fecha)}
      </div>
    );
  };

  const customizedMarker = (evento) => {
    const finalizada =
      evento.tipo === "actividad" && !!evento.fechafinalizacion;
    const esEmision = evento.tipo === "emision";
    const esSolicitud = evento.tipo === "solicitud";
    const enProceso = evento.tipo === "actividad" && !evento.fechafinalizacion;

    let icono = "pi pi-check"; // Por defecto
    let color = "var(--verde)";

    if (evento.tipo === "cambioEstado") {
      icono = evento.vigente
        ? "pi pi-check"
        : evento.bajadefinitiva
          ? "pi pi-times"
          : "pi pi-exclamation-circle";
      color = evento.vigente
        ? "var(--verde)"
        : evento.bajadefinitiva
          ? "var(--rojo)"
          : "var(--amarillo)";
    } else if (esSolicitud) {
      icono = "pi pi-inbox";
      color = "var(--principal)";
    } else if (esEmision) {
      icono = "pi pi-file";
      color = "var(--principal)";
    } else if (enProceso) {
      icono = "pi pi-exclamation-circle";
      color = "var(--amarillo)";
    } else if (finalizada) {
      icono = "pi pi-check";
      color = "var(--verde)";
    }

    return (
      <span
        css={{
          display: "flex",
          width: "2rem",
          height: "2rem",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          borderRadius: "50%",
          backgroundColor: color,
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.2)",
        }}
      >
        <i className={icono}></i>
      </span>
    );
  };

  return (
    <div css={{ padding: "1em" }}>
      {eventos && eventos.length > 0 ? (
        <Timeline
          value={eventos}
          opposite={customizedOpposite}
          content={customizedContent}
          marker={customizedMarker}
        />
      ) : (
        <p>No hay actividades relacionadas</p>
      )}

      <ContenedorDetalle
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        {...propsDetalle}
      >
        {actividadSeleccionada && (
          <div>
            {/* En la ventana chica el nombre va como título de la ventana */}
            {!detalleComoDialogo && (
              <Titulo>{actividadSeleccionada.actividadtipo?.nombre}</Titulo>
            )}

            <div css={{ marginBottom: "1.5em" }}>
              <div
                css={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "1em",
                }}
              >
                <div>
                  <small
                    css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                  >
                    Fecha de creación
                  </small>
                  <div>{formatFecha(actividadSeleccionada.fechacreacion)}</div>
                </div>
                <div>
                  <small
                    css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                  >
                    Fecha de finalización
                  </small>
                  <div>
                    {actividadSeleccionada.fechafinalizacion
                      ? formatFecha(actividadSeleccionada.fechafinalizacion)
                      : "En curso"}
                  </div>
                </div>
              </div>
            </div>

            {actividadSeleccionada.actividadpaso?.length > 0 && (
              <div>
                <Titulo>Pasos de la actividad</Titulo>
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1em",
                  }}
                >
                  {actividadSeleccionada.actividadpaso.map((paso) => (
                    <div
                      key={paso.id}
                      css={{
                        padding: "1em",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <div
                        css={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.75em",
                        }}
                      >
                        <strong>
                          {paso.nombre ||
                            paso.pasoTipo
                              ?.nombre ||
                            "Paso"}
                        </strong>
                        {!paso.actual ? (
                          <span
                            css={{
                              fontSize: "0.85em",
                              color: "var(--verde)",
                            }}
                          >
                            <i
                              className="pi pi-check"
                              css={{ marginRight: "0.25em" }}
                            ></i>
                            Finalizado
                          </span>
                        ) : (
                          <span
                            css={{
                              fontSize: "0.85em",
                              color: "var(--amarillo)",
                            }}
                          >
                            <i
                              className="pi pi-clock"
                              css={{ marginRight: "0.25em" }}
                            ></i>
                            En curso
                          </span>
                        )}
                      </div>

                      {paso.actividadpaso_x_auditor?.length > 0 && (() => {
                        const pasoCerrado = !paso.actual && paso.actividadpaso_x_auditor.some(
                          (a) => a.actividadpasodecision?.length > 0
                        );
                        const auditoresParaMostrar = pasoCerrado
                          ? paso.actividadpaso_x_auditor.filter(
                              (a) => a.actividadpasodecision?.length > 0
                            )
                          : paso.actividadpaso_x_auditor.filter(
                              (a) => a.activo !== false
                            );
                        if (auditoresParaMostrar.length === 0) return null;
                        return (
                        <div css={{ marginTop: "0.75em" }}>
                          <small
                            css={{
                              color: "var(--grisTextos)",
                              fontWeight: "600",
                              display: "block",
                              marginBottom: "0.5em",
                            }}
                          >
                            {pasoCerrado ? "Decisor" : "Colaboradores asignados"}
                          </small>
                          <div
                            css={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.25em",
                              marginBottom: "0.75em",
                            }}
                          >
                            {auditoresParaMostrar.map(
                              (asignacion, idx) => {
                                const formaciones = formacionPorAuditor?.[asignacion.auditor?.id];
                                return (
                                <div
                                  key={idx}
                                  css={{
                                    fontSize: "0.9em",
                                  }}
                                >
                                  <div css={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5em",
                                  }}>
                                    <i
                                      className="pi pi-user"
                                      css={{
                                        color: "var(--grisTextos)",
                                        fontSize: "0.85em",
                                      }}
                                    ></i>
                                    <span>
                                      {asignacion.auditor?.nombre}{" "}
                                      {asignacion.auditor?.apellido}
                                    </span>
                                    {(asignacion.decisor ||
                                      asignacion.enformacion) && (
                                      <span
                                        css={{
                                          fontSize: "0.8em",
                                          backgroundColor: asignacion.decisor
                                            ? "var(--principal)"
                                            : "var(--amarillo)",
                                          color: asignacion.decisor
                                            ? "white"
                                            : "black",
                                          padding: "0.1em 0.4em",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {asignacion.decisor
                                          ? "Decisor"
                                          : "En formación"}
                                      </span>
                                    )}
                                  </div>
                                  {formaciones?.length > 0 && (
                                    <div css={{
                                      marginLeft: "1.6em",
                                      marginTop: "0.4em",
                                      marginBottom: "0.4em",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "0.3em",
                                    }}>
                                      <small css={{ color: "var(--grisTextos)", fontWeight: "600" }}>
                                        Formación relevante
                                      </small>
                                      {formaciones.map((f) => (
                                        <div
                                          key={f.id}
                                          css={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5em",
                                            fontSize: "0.9em",
                                            color: "var(--grisTextos)",
                                          }}
                                        >
                                          <i className="pi pi-check-circle" css={{ color: "var(--verde)", fontSize: "0.85em" }}></i>
                                          <span>{f.RequerimientoFormacionColaborador?.nombre}</span>
                                          <span css={{ color: "#999" }}>
                                            {formatFecha(f.fecha)}
                                          </span>
                                          {f.archivo && onDescargarEvidencia && (
                                            <Boton
                                              callback={() => {
                                                setEvidenciaDescargando(f.archivo.id);
                                                onDescargarEvidencia(
                                                  f.archivo.hash,
                                                  f.archivo.nombredescarga,
                                                  () => setEvidenciaDescargando(null),
                                                );
                                              }}
                                              texto
                                              chico
                                              loader={evidenciaDescargando === f.archivo.id}
                                            >
                                              Evidencia
                                            </Boton>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );},
                            )}
                          </div>

                          {paso.actividadpaso_x_auditor.some(
                            (a) => a.actividadpasodecision?.length > 0,
                          ) && (
                            <div>
                              <small
                                css={{
                                  color: "var(--grisTextos)",
                                  fontWeight: "600",
                                  display: "block",
                                  marginBottom: "0.5em",
                                }}
                              >
                                Decisión del paso
                              </small>
                              {paso.actividadpaso_x_auditor
                                .filter(
                                  (a) => a.actividadpasodecision?.length > 0,
                                )
                                .map((asignacion) =>
                                  asignacion.actividadpasodecision.map(
                                    (decision) => (
                                      <div
                                        key={decision.id}
                                        css={{
                                          padding: "0.75em",
                                          backgroundColor: decision.aprobado
                                            ? "rgba(76, 175, 80, 0.1)"
                                            : "rgba(244, 67, 54, 0.1)",
                                          borderRadius: "4px",
                                          borderLeft: `3px solid ${
                                            decision.aprobado
                                              ? "var(--verde)"
                                              : "var(--rojo)"
                                          }`,
                                        }}
                                      >
                                        <div
                                          css={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5em",
                                            marginBottom: decision.comentarios
                                              ? "0.5em"
                                              : 0,
                                          }}
                                        >
                                          <i
                                            className={
                                              decision.aprobado
                                                ? "pi pi-check"
                                                : "pi pi-times"
                                            }
                                            css={{
                                              color: decision.aprobado
                                                ? "var(--verde)"
                                                : "var(--rojo)",
                                            }}
                                          ></i>
                                          <span css={{ fontWeight: "500" }}>
                                            {decision.aprobado
                                              ? "Aprobado"
                                              : "Rechazado"}
                                          </span>
                                        </div>
                                        {decision.comentarios && (
                                          <p
                                            css={{
                                              margin: "0 0 0.5em 0",
                                              fontSize: "0.9em",
                                              whiteSpace: "pre-wrap",
                                            }}
                                          >
                                            {decision.comentarios}
                                          </p>
                                        )}
                                        <small css={{ color: "#666" }}>
                                          {asignacion.auditor?.nombre}{" "}
                                          {asignacion.auditor?.apellido} -{" "}
                                          {formatFecha(decision.fechadecision)}
                                        </small>
                                      </div>
                                    ),
                                  ),
                                )}
                            </div>
                          )}
                        </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {actividadSeleccionada.hallazgos?.length > 0 && (
              <div>
                <Titulo>Hallazgos</Titulo>
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1em",
                  }}
                >
                  {actividadSeleccionada.hallazgos.map((hallazgo) => (
                    <div
                      key={hallazgo.id}
                      css={{
                        padding: "1em",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <div
                        css={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "0.5em",
                        }}
                      >
                        <strong>{hallazgo.resumen}</strong>
                        <span
                          css={{
                            fontSize: "0.8em",
                            backgroundColor: hallazgo.fechaCierre
                              ? "var(--verde)"
                              : hallazgo.hallazgoTipo?.clienteResponde
                                ? "var(--amarillo)"
                                : "var(--principal)",
                            color: hallazgo.fechaCierre || !hallazgo.hallazgoTipo?.clienteResponde
                              ? "white"
                              : "black",
                            padding: "0.15em 0.5em",
                            borderRadius: "4px",
                            whiteSpace: "nowrap",
                            marginLeft: "0.5em",
                          }}
                        >
                          {hallazgo.fechaCierre
                            ? "Cerrado"
                            : hallazgo.hallazgoTipo?.nombre}
                        </span>
                      </div>

                      <div
                        css={{
                          fontSize: "0.9em",
                          color: "var(--grisTextos)",
                          marginBottom: "0.5em",
                        }}
                      >
                        {hallazgo.hallazgoTipo?.nombre}
                        {" - "}
                        {formatFecha(hallazgo.fechaCreacion)}
                      </div>

                      {hallazgo.texto && (
                        <div
                          css={{
                            fontSize: "0.9em",
                            marginBottom: "0.75em",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {hallazgo.texto}
                        </div>
                      )}

                      {hallazgo.auditorCreador && (
                        <small css={{ color: "var(--grisTextos)", display: "block", marginBottom: "0.75em" }}>
                          <i className="pi pi-user" css={{ marginRight: "0.3em" }}></i>
                          Creado por {hallazgo.auditorCreador.nombre} {hallazgo.auditorCreador.apellido}
                        </small>
                      )}

                      {hallazgo.fechaCierre && (
                        <div
                          css={{
                            fontSize: "0.85em",
                            color: "var(--verde)",
                            marginBottom: "0.75em",
                          }}
                        >
                          <i className="pi pi-check" css={{ marginRight: "0.3em" }}></i>
                          Cerrado el {formatFecha(hallazgo.fechaCierre)}
                          {hallazgo.auditorRevisor && (
                            <span>
                              {" "}por {hallazgo.auditorRevisor.nombre} {hallazgo.auditorRevisor.apellido}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Interacciones */}
                      {hallazgo.interacciones?.length > 0 && (
                        <div css={{ marginTop: "0.5em" }}>
                          <small
                            css={{
                              color: "var(--grisTextos)",
                              fontWeight: "600",
                              display: "block",
                              marginBottom: "0.5em",
                            }}
                          >
                            Interacciones
                          </small>
                          <div
                            css={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.5em",
                            }}
                          >
                            {hallazgo.interacciones.map((interaccion, idx) => {
                              const esCliente = interaccion.cliente;
                              const esCierre = interaccion.cierre;
                              const borderColor = esCierre
                                ? "var(--verde)"
                                : esCliente
                                  ? "var(--principal)"
                                  : "var(--grisTextos)";
                              return (
                                <div
                                  key={idx}
                                  css={{
                                    padding: "0.75em",
                                    backgroundColor: "white",
                                    borderRadius: "4px",
                                    borderLeft: `3px solid ${borderColor}`,
                                  }}
                                >
                                  <div
                                    css={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "0.4em",
                                    }}
                                  >
                                    <small css={{ fontWeight: "600" }}>
                                      {interaccion.usuario}
                                      {esCierre && (
                                        <span css={{ color: "var(--verde)", marginLeft: "0.5em" }}>
                                          (Cierre)
                                        </span>
                                      )}
                                    </small>
                                    <small css={{ color: "#999" }}>
                                      {interaccion.fecha && formatFecha(interaccion.fecha)}
                                    </small>
                                  </div>

                                  {interaccion.textos?.map((texto, tIdx) => (
                                    <div key={tIdx} css={{ marginBottom: "0.3em" }}>
                                      <small
                                        css={{
                                          color: "var(--grisTextos)",
                                          fontWeight: "600",
                                          display: "block",
                                        }}
                                      >
                                        {texto.nombre}
                                      </small>
                                      <div
                                        css={{
                                          fontSize: "0.9em",
                                          whiteSpace: "pre-wrap",
                                        }}
                                      >
                                        {texto.contenido}
                                      </div>
                                    </div>
                                  ))}

                                  {onDescargarEvidencia &&
                                    evidenciasDeInteraccion(interaccion)
                                      .filter((evidencia) => evidencia.hash)
                                      .map((evidencia) => (
                                    <div key={evidencia.archivoId} css={{ marginTop: "0.4em" }}>
                                      <Boton
                                        callback={() => {
                                          setEvidenciaDescargando(evidencia.archivoId);
                                          onDescargarEvidencia(
                                            evidencia.hash,
                                            evidencia.nombre || "evidencia",
                                            () => setEvidenciaDescargando(null),
                                          );
                                        }}
                                        texto
                                        chico
                                        loader={evidenciaDescargando === evidencia.archivoId}
                                      >
                                        <i className="pi pi-download" css={{ marginRight: "0.3em" }}></i>
                                        {evidencia.nombre || "Descargar evidencia"}
                                      </Boton>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </ContenedorDetalle>

      <Sidebar
        visible={sidebarSolicitudVisible}
        onHide={() => setSidebarSolicitudVisible(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {solicitudSeleccionada && (
          <div>
            <Titulo>Solicitud de certificación</Titulo>

            <div css={{ marginBottom: "1.5em" }}>
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1em",
                }}
              >
                {solicitudSeleccionada.nombrefantasia && (
                  <div>
                    <small
                      css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                    >
                      Nombre fantasía
                    </small>
                    <div>{solicitudSeleccionada.nombrefantasia}</div>
                  </div>
                )}

                {solicitudSeleccionada.fecha && (
                  <div>
                    <small
                      css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                    >
                      Fecha de solicitud
                    </small>
                    <div>{formatFecha(solicitudSeleccionada.fecha)}</div>
                  </div>
                )}

                {solicitudSeleccionada.certificadotipo?.nombre && (
                  <div>
                    <small
                      css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                    >
                      Tipo de certificado
                    </small>
                    <div>{solicitudSeleccionada.certificadotipo.nombre}</div>
                  </div>
                )}

                {solicitudSeleccionada.cliente?.razonsocial && (
                  <div>
                    <small
                      css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                    >
                      Cliente
                    </small>
                    <div>{solicitudSeleccionada.cliente.razonsocial}</div>
                  </div>
                )}

                {solicitudSeleccionada.sitioFabricante?.nombre && (
                  <div>
                    <small
                      css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                    >
                      Fabricante
                    </small>
                    <div>{solicitudSeleccionada.sitioFabricante.nombre}</div>
                  </div>
                )}

                {solicitudSeleccionada.sitioFabrica?.nombre && (
                  <div>
                    <small
                      css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                    >
                      Fábrica
                    </small>
                    <div>{solicitudSeleccionada.sitioFabrica.nombre}</div>
                  </div>
                )}

                {solicitudSeleccionada.codigocontratoprevio &&
                  solicitudSeleccionada.codigocontratoprevio !==
                    "undefined" && (
                    <div>
                      <small
                        css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                      >
                        Código de contrato previo
                      </small>
                      <div>{solicitudSeleccionada.codigocontratoprevio}</div>
                    </div>
                  )}

                <div>
                  <small
                    css={{ color: "var(--grisTextos)", fontWeight: "600" }}
                  >
                    Estado
                  </small>
                  <div>
                    {solicitudSeleccionada.procesadabool || solicitudSeleccionada.procesada ? (
                      <span css={{ color: "var(--verde)" }}>
                        <i
                          className="pi pi-check"
                          css={{ marginRight: "0.25em" }}
                        ></i>
                        Procesada
                      </span>
                    ) : (
                      <span css={{ color: "var(--amarillo)" }}>
                        <i
                          className="pi pi-clock"
                          css={{ marginRight: "0.25em" }}
                        ></i>
                        Pendiente
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {revisorSolicitud?.nombre && (
              <div>
                <h3>Revisor de solicitud</h3>
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5em",
                    padding: "0.75em",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                  }}
                >
                  <i
                    className="pi pi-user"
                    css={{ color: "var(--grisTextos)" }}
                  ></i>
                  <span>
                    {revisorSolicitud.nombre} {revisorSolicitud.apellido || ""}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </Sidebar>
    </div>
  );
}
